import React from 'react';
import {
	BarChartOutlined,
	BarsOutlined,
	CalendarOutlined,
	CameraOutlined,
	CommentOutlined,
	DashboardOutlined,
	EditOutlined,
	EnvironmentOutlined,
	FolderOutlined,
	FileTextOutlined,
	HomeOutlined,
	LineChartOutlined,
	LikeOutlined,
	PictureOutlined,
	QuestionOutlined,
	SettingOutlined,
	ShareAltOutlined,
	StarOutlined,
	SmileOutlined,
	SoundOutlined,
	SyncOutlined,
	TagOutlined,
	TeamOutlined,
	TrophyOutlined,
	UserOutlined,
	VideoCameraOutlined,
} from '@ant-design/icons';
import { HiOutlineMicrophone } from 'react-icons/hi';
import { Report, ThumbsUp, Rectangles } from '@blueprintnyc/bpnyc-common-admin/dist/icons';

const navItems = event => ({
	dashboard: {
		name: 'Dashboard',
		icon: <DashboardOutlined />,
		path: `/events/${event.id}/dashboard`,
	},
	settings: {
		name: 'Settings',
		icon: <SettingOutlined />,
		path: `/events/${event.id}/settings`,
	},
	attendees: {
		name: 'People',
		role: 'ATTENDEES',
		icon: <UserOutlined />,
		path: `/events/${event.id}/attendees`,
	},
	groups: {
		name: 'Groups',
		role: 'ATTENDEES',
		icon: <TeamOutlined />,
		path: `/events/${event.id}/groups`,
	},
	agenda: {
		name: 'Agenda',
		role: 'AGENDA',
		icon: <CalendarOutlined />,
		path: `/events/${event.id}/agenda`,
	},
	layout: {
		name: 'Layout / Content',
		role: 'APP_DESIGN',
		icon: <Rectangles />,
		type: 'submenu',
		items: [
			{
				name: 'Navigation',
				role: 'APP_DESIGN',
				icon: <BarsOutlined />,
				path: `/events/${event.id}/navigation`,
			},
			{
				name: 'Theme',
				role: 'APP_DESIGN',
				icon: <EditOutlined />,
				path: `/events/${event.id}/theme`,
			},
			{
				name: 'Home Screen',
				role: 'APP_DESIGN',
				icon: <HomeOutlined />,
				path:
					event.active_homescreen === 'WIDGET'
						? `/events/${event.id}/widgetHome`
						: `/events/${event.id}/home`,
			},
			{
				name: 'Pages',
				role: 'APP_DESIGN',
				icon: <FileTextOutlined />,
				path: `/events/${event.id}/pages`,
			},
			{
				name: 'Materials',
				role: 'APP_DESIGN',
				icon: <FolderOutlined />,
				path: `/events/${event.id}/materials`,
			},
			{
				name: 'Media',
				role: 'APP_DESIGN',
				icon: <PictureOutlined />,
				path: `/events/${event.id}/media`,
			},
		],
	},
	social: {
		name: 'Engagement',
		role: 'SOCIAL_FEED',
		icon: <ThumbsUp />,
		type: 'submenu',
		items: [
			{
				name: 'Social Feed',
				role: 'SOCIAL_FEED',
				icon: <LikeOutlined />,
				path: `/events/${event.id}/socialFeed`,
			},
			{
				name: 'Kudos',
				role: 'SOCIAL_FEED',
				icon: <SmileOutlined />,
				path: `/events/${event.id}/kudos`,
			},
			{
				name: 'Badges',
				role: 'SOCIAL_FEED',
				icon: <TagOutlined />,
				path: `/events/${event.id}/badges`,
			},
			{
				name: 'Photo Booths',
				role: 'SOCIAL_FEED',
				icon: <CameraOutlined />,
				path: `/events/${event.id}/photobooth`,
			},
			//Commenting out for code review, but feature will still exist at /ai-photobooth
			// {
			// 	name: 'AI Photobooths',
			// 	role: 'SOCIAL_FEED',
			// 	icon: <CameraOutlined />,
			// 	path: `/events/${event.id}/ai-photobooth`,
			// },
			{
				name: 'Quizzes',
				role: 'SOCIAL_FEED',
				icon: <QuestionOutlined />,
				path: `/events/${event.id}/quizzes`,
			},
			{
				name: 'Points',
				role: 'SOCIAL_FEED',
				icon: <StarOutlined />,
				path: `/events/${event.id}/points`,
			},
			{
				name: 'Leaderboard',
				role: 'SOCIAL_FEED',
				icon: <TrophyOutlined />,
				path: `/events/${event.id}/leaderboards`,
			},
			{
				name: 'Podcasts',
				role: 'SOCIAL_FEED',
				icon: <HiOutlineMicrophone />,
				path: `/events/${event.id}/podcasts`,
			},
		],
	},
	plugins: {
		name: 'Live',
		role: 'PLUGINS',
		icon: <CommentOutlined />,
		type: 'submenu',
		items: [
			{
				name: 'Live Q&A',
				role: 'PLUGINS',
				icon: <CommentOutlined />,
				path: `/events/${event.id}/lqs`,
			},
			{
				name: 'Live Polling',
				role: 'PLUGINS',
				icon: <BarChartOutlined />,
				path: `/events/${event.id}/polls`,
			},
			{
				name: 'Broadcasts',
				role: 'PLUGINS',
				icon: <VideoCameraOutlined />,
				path: `/events/${event.id}/broadcasts`,
			},
			{
				name: 'Check Ins',
				role: 'PLUGINS',
				icon: <EnvironmentOutlined />,
				path: `/events/${event.id}/checkins`,
			},
			{
				name: 'Connections',
				role: 'PLUGINS',
				icon: <ShareAltOutlined />,
				path: `/events/${event.id}/connections`,
			},
		],
	},
	reports: {
		name: 'Reports',
		role: 'ANALYTICS',
		type: 'submenu',
		icon: <Report />,
		items: [
			{
				name: 'Audit',
				role: 'ANALYTICS',
				icon: <LineChartOutlined />,
				path: `/events/${event.id}/audit`,
			},
			{
				name: 'Syncs',
				role: 'ANALYTICS',
				icon: <SyncOutlined />,
				path: `/events/${event.id}/report`,
			},
		],
	},
	announcements: {
		name: 'Announcements',
		role: 'ANNOUNCEMENTS',
		icon: <SoundOutlined />,
		path: `/events/${event.id}/announcements`,
	},
});

const notForSaaS = ['Broadcasts', 'Photo Booths', 'Media'];

const eventNavigation = event => {
	const items = navItems(event);
	return [
		items.dashboard,
		items.settings,
		items.attendees,
		items.groups,
		items.agenda,
		items.layout,
		items.social,
		items.plugins,
		items.reports,
		items.announcements,
	];
};

const standaloneEventNavigation = event => {
	const items = navItems(event);
	return [
		items.dashboard,
		items.settings,
		items.attendees,
		{
			name: 'Home Screen',
			role: 'APP_DESIGN',
			icon: <HomeOutlined />,
			path: `/events/${event.id}/home`,
		},
		items.plugins,
		items.reports,
	];
};

export const getEventNavigation = (event, roles, isSaaS) => {
	const filterNavItem = navItem => {
		return (
			navItem &&
			(!navItem.role || roles.includes(navItem.role)) &&
			(!isSaaS || !notForSaaS.includes(navItem.name))
		);
	};
	const unfilteredMenu =
		event.single_page_app === 'TRUE' ? standaloneEventNavigation(event) : eventNavigation(event);
	return unfilteredMenu.filter(filterNavItem).map(navItem => {
		if (navItem.type === 'submenu') {
			navItem.items = navItem.items.filter(filterNavItem);
		}
		return navItem;
	});
};
